import { api, publicApi } from './client'
import type {
  ActivityEntry, AdminUser, AuditEntry, CurrentUser, EditorConfigResponse, FileItem, FileKind, ResolvedAsset, PermissionDef, AccessEntry, AdminProject, Role, RoleInput, RoleScope, FileStats, FileVersion,
  LoginResponse, Member, Note, NoteSummary, NoteVersion, NoteVersionSummary,
  MailEventSetting, MailLogEntry, MailSettings, MailSettingsInput, PasswordLinkInfo,
  Folder, HomeSummary, Page, Project, ProjectScope, ProjectTeam, SearchHit, Session, ShareLink, ShareTargetType, SharedContent,
  Tag, Team, TeamDetail, TeamSummary, UserSummary, Visibility, Comment,
  DailyReport, RunStatus, TestCaseFilter, TestCaseResult, TestImportResult, TestModuleProgress,
  TestQueue, TestResultEvent, TestRun, TestStatus, TestSuite, TesterActivity,
  VaultEntry, VaultEntryInput, VaultEntrySummary,
  NotificationList, OfferDetail, OfferDocument, OfferInput, OfferNote, OfferOverview, OfferReminder, OfferReminderInput, OfferStatus,
  OfferSummary,
  DeployConnectionTest, DeployCredential, DeployCredentialInput, DeployEnvironment, DeployEnvironmentInput,
  DeployJob, DeployJobInput, DeployLogPage, DeployOverview, DeployRun, DeployRunSummary, DeployServer, DeployServerInput,
  AssignableTeam, ChecklistItem, TaskAttachment, TaskComment, TaskDetail, TaskHistoryEntry,
  Board, ColumnInput, Overview, ProjectProgress, ProjectStage, Task, TaskColumn, TaskInput, TaskList,
} from './types'

/** Content-Disposition başlığından dosya adını çıkarır. */
function dosyaAdi(disposition: unknown): string {
  const s = typeof disposition === 'string' ? disposition : ''
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(s)
  if (utf8) return decodeURIComponent(utf8[1])
  const plain = /filename="?([^";]+)"?/i.exec(s)
  return plain ? plain[1] : 'indirilen-dosya'
}

// --- auth -------------------------------------------------------------
export const authApi = {
  login: (username: string, password: string, totpCode?: string) =>
    api.post<LoginResponse>('/api/auth/login', { username, password, totpCode })
      .then((r) => r.data),
  refresh: () => api.post<LoginResponse>('/api/auth/refresh').then((r) => r.data),
  logout: () => api.post('/api/auth/logout').then((r) => r.data),
  logoutAll: () => api.post('/api/auth/logout-all').then((r) => r.data),
  me: () => api.get<CurrentUser>('/api/auth/me').then((r) => r.data),
  /** Kendi adı ve e-postası. Boş e-posta adresi siler. */
  updateMe: (body: { displayName: string; email: string }) =>
    api.patch<CurrentUser>('/api/auth/me', body).then((r) => r.data),
  /** Kendi açık oturumları. "Bu cihaz" işareti refresh cookie'sinden gelir. */
  sessions: () => api.get<Session[]>('/api/auth/sessions').then((r) => r.data),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/api/auth/change-password', { currentPassword, newPassword }).then((r) => r.data),

  // Parola bağlantıları (davet, parolamı unuttum). Oturum YOKTUR: publicApi token eklemez,
  // 401'de yenileme denemez.
  /** Giriş ekranı "Parolamı unuttum" bağlantısını göstersin mi? */
  options: () => publicApi.get<{ passwordReset: boolean }>('/api/auth/options').then((r) => r.data),
  /** Hesap olsun olmasın aynı yanıt döner (hesap varlığı ele verilmez). */
  forgotPassword: (identifier: string) =>
    publicApi.post<{ message: string }>('/api/auth/forgot-password', { identifier }).then((r) => r.data),
  inspectPasswordLink: (token: string) =>
    publicApi.post<PasswordLinkInfo>('/api/auth/password-link/inspect', { token }).then((r) => r.data),
  completePasswordLink: (token: string, newPassword: string) =>
    publicApi.post('/api/auth/reset-password', { token, newPassword }).then((r) => r.data),

  // İki adımlı doğrulama
  setup2fa: () =>
    api.post<{ secret: string; otpauthUrl: string }>('/api/auth/2fa/setup').then((r) => r.data),
  /** Mevcut parola da istenir: çalınmış oturum 2FA'yı başka cihaza bağlayamasın. */
  enable2fa: (code: string, password: string) =>
    api.post<{ recoveryCodes: string[] }>('/api/auth/2fa/enable', { code, password }).then((r) => r.data),
  disable2fa: (password: string) =>
    api.post('/api/auth/2fa/disable', { password }).then((r) => r.data),
  /** Kurtarma kodlarını yeniden üretir (eskileri iptal eder); yeni kodlar bir kez döner. */
  regenerateRecoveryCodes: (password: string) =>
    api.post<{ recoveryCodes: string[] }>('/api/auth/2fa/recovery-codes', { password }).then((r) => r.data),
  /** Kalan (kullanılmamış) kurtarma kodu sayısı. */
  recoveryCodesRemaining: () =>
    api.get<{ remaining: number }>('/api/auth/2fa/recovery-codes').then((r) => r.data),
}

// --- ana sayfa ---------------------------------------------------------
export const homeApi = {
  /** Saat dilimi gönderilir: "bugün" kullanıcının gece yarısına göre bölünsün. */
  summary: () =>
    api.get<HomeSummary>('/api/home', {
      params: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
    }).then((r) => r.data),
}

// --- projeler ---------------------------------------------------------
export const projectApi = {
  list: (params: { q?: string; visibility?: Visibility; scope?: ProjectScope; page?: number; size?: number }) =>
    api.get<Page<Project>>('/api/projects', { params }).then((r) => r.data),
  trash: () => api.get<Page<Project>>('/api/projects/trash').then((r) => r.data),
  get: (id: string) => api.get<Project>(`/api/projects/${id}`).then((r) => r.data),
  create: (body: { name: string; description?: string; visibility: Visibility }) =>
    api.post<Project>('/api/projects', body).then((r) => r.data),
  update: (id: string, body: { name?: string; description?: string; version?: number }) =>
    api.patch<Project>(`/api/projects/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/projects/${id}`).then((r) => r.data),
  restore: (id: string) => api.post<Project>(`/api/projects/${id}/restore`).then((r) => r.data),
  setVisibility: (id: string, visibility: Visibility, force = false) =>
    api.put<Project>(`/api/projects/${id}/visibility`, { visibility }, { params: { force } })
      .then((r) => r.data),

  members: (id: string) => api.get<Member[]>(`/api/projects/${id}/members`).then((r) => r.data),
  /** Erişen herkes ve erişim kaynakları (doğrudan + ekipler). */
  access: (id: string) => api.get<AccessEntry[]>(`/api/projects/${id}/access`).then((r) => r.data),
  /** Rol kimliğiyle ({@code roleId}); sunucu atayanın izinlerini aşan rolü reddeder (403). */
  addMember: (id: string, userId: string, roleId: string) =>
    api.post<Member>(`/api/projects/${id}/members`, { userId, roleId }).then((r) => r.data),
  changeMemberRole: (id: string, userId: string, roleId: string) =>
    api.patch<Member>(`/api/projects/${id}/members/${userId}`, { roleId }).then((r) => r.data),
  removeMember: (id: string, userId: string) =>
    api.delete(`/api/projects/${id}/members/${userId}`).then((r) => r.data),
  transferOwnership: (id: string, newOwnerId: string) =>
    api.post(`/api/projects/${id}/transfer-ownership`, null, { params: { newOwnerId } })
      .then((r) => r.data),

  /** Projeye atanmış ekipler (üyeleriyle). */
  teams: (id: string) => api.get<ProjectTeam[]>(`/api/projects/${id}/teams`).then((r) => r.data),
  assignTeam: (id: string, teamId: string, roleId: string) =>
    api.post<ProjectTeam>(`/api/projects/${id}/teams`, { teamId, roleId }).then((r) => r.data),
  changeTeamRole: (id: string, teamId: string, roleId: string) =>
    api.patch(`/api/projects/${id}/teams/${teamId}`, { roleId }).then((r) => r.data),
  removeTeam: (id: string, teamId: string) =>
    api.delete(`/api/projects/${id}/teams/${teamId}`).then((r) => r.data),

  activity: (id: string, page = 0, size = 30) =>
    api.get<Page<ActivityEntry>>(`/api/projects/${id}/activity`, { params: { page, size } })
      .then((r) => r.data),
}

// --- notlar -----------------------------------------------------------
export const noteApi = {
  list: (projectId: string,
         params: { q?: string; folderId?: string; page?: number; size?: number } = {}) =>
    api.get<Page<NoteSummary>>(`/api/projects/${projectId}/notes`, { params }).then((r) => r.data),
  trash: (projectId: string) =>
    api.get<Page<NoteSummary>>(`/api/projects/${projectId}/notes/trash`).then((r) => r.data),
  get: (id: string) => api.get<Note>(`/api/notes/${id}`).then((r) => r.data),
  create: (projectId: string,
           body: { title?: string; contentJson?: Record<string, unknown>; folderId?: string }) =>
    api.post<Note>(`/api/projects/${projectId}/notes`, body).then((r) => r.data),
  update: (id: string, body: { title?: string; contentJson: Record<string, unknown>; version?: number }) =>
    api.put<Note>(`/api/notes/${id}`, body).then((r) => r.data),
  rename: (id: string, title: string) =>
    api.patch<Note>(`/api/notes/${id}/title`, null, { params: { title } }).then((r) => r.data),
  togglePin: (id: string) => api.post<Note>(`/api/notes/${id}/pin`).then((r) => r.data),
  /** folderId null ise not kök seviyeye taşınır. */
  move: (id: string, folderId: string | null) =>
    api.patch<Note>(`/api/notes/${id}/folder`, { folderId }).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/notes/${id}`).then((r) => r.data),
  restore: (id: string) => api.post<Note>(`/api/notes/${id}/restore`).then((r) => r.data),

  versions: (id: string) =>
    api.get<Page<NoteVersionSummary>>(`/api/notes/${id}/versions`).then((r) => r.data),
  version: (id: string, no: number) =>
    api.get<NoteVersion>(`/api/notes/${id}/versions/${no}`).then((r) => r.data),
  createVersion: (id: string, summary?: string) =>
    api.post<NoteVersionSummary>(`/api/notes/${id}/versions`, null, { params: { summary } })
      .then((r) => r.data),
  restoreVersion: (id: string, no: number) =>
    api.post<Note>(`/api/notes/${id}/versions/${no}/restore`).then((r) => r.data),

  /**
   * Notu dosya olarak indirir.
   *
   * <p>Düz bir bağlantı kullanılamaz: access token yalnızca bellekte durur ve
   * tarayıcının kendi başlattığı istek Authorization başlığı taşımaz. Bu
   * yüzden içerik axios ile çekilip istemci tarafında indirtilir.
   */
  download: async (id: string, format: 'html' | 'markdown') => {
    const res = await api.get(`/api/notes/${id}/export`, {
      params: { format },
      responseType: 'blob',
    })
    return { blob: res.data as Blob, filename: dosyaAdi(res.headers['content-disposition']) }
  },

  tags: (id: string) => api.get<Tag[]>(`/api/notes/${id}/tags`).then((r) => r.data),
  setTags: (id: string, tagIds: string[]) =>
    api.put<Tag[]>(`/api/notes/${id}/tags`, { tagIds }).then((r) => r.data),
}

// --- dosyalar ---------------------------------------------------------
export const fileApi = {
  list: (projectId: string,
         params: { q?: string; folderId?: string; rootOnly?: boolean; kind?: FileKind; page?: number; size?: number } = {}) =>
    api.get<Page<FileItem>>(`/api/projects/${projectId}/files`, { params }).then((r) => r.data),
  stats: (projectId: string) => api.get<FileStats>(`/api/projects/${projectId}/files/stats`).then((r) => r.data),
  trash: (projectId: string) =>
    api.get<Page<FileItem>>(`/api/projects/${projectId}/files/trash`).then((r) => r.data),
  get: (id: string) => api.get<FileItem>(`/api/files/${id}`).then((r) => r.data),

  /**
   * @param onConflict ad klasörde varsa: `RENAME` "a (2).png" üretir (varsayılan),
   *                   `VERSION` var olan dosyaya yeni sürüm yazar (klasör yeniden yükleme).
   */
  upload: (projectId: string, file: File, folderId?: string | null,
           onProgress?: (pct: number) => void, onConflict: 'RENAME' | 'VERSION' = 'RENAME') => {
    const form = new FormData()
    form.append('file', file)
    return api
      .post<FileItem>(`/api/projects/${projectId}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { ...(folderId ? { folderId } : {}), onConflict },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
        },
      })
      .then((r) => r.data)
  },

  uploadAsset: (projectId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api
      .post<FileItem>(`/api/projects/${projectId}/assets`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },

  uploadVersion: (fileId: string, file: File, comment?: string) => {
    const form = new FormData()
    form.append('file', file)
    return api
      .post<FileItem>(`/api/files/${fileId}/versions`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { comment },
      })
      .then((r) => r.data)
  },

  rename: (id: string, name: string) =>
    api.patch<FileItem>(`/api/files/${id}/name`, { name }).then((r) => r.data),
  /** folderId null ise dosya kök seviyeye taşınır. */
  move: (id: string, folderId: string | null) =>
    api.patch<FileItem>(`/api/files/${id}/folder`, { folderId }).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/files/${id}`).then((r) => r.data),
  restore: (id: string) => api.post<FileItem>(`/api/files/${id}/restore`).then((r) => r.data),

  versions: (id: string) => api.get<FileVersion[]>(`/api/files/${id}/versions`).then((r) => r.data),
  restoreVersion: (id: string, no: number) =>
    api.post<FileItem>(`/api/files/${id}/versions/${no}/restore`).then((r) => r.data),

  downloadUrl: (id: string) => `/api/files/${id}/download`,
  versionDownloadUrl: (id: string, no: number) => `/api/files/${id}/versions/${no}/download`,
  previewUrl: (id: string) => `/api/files/${id}/preview`,
  /**
   * HTML önizlemesi: göreli yolları ("img/a.png") dosyanın klasörüne göre aynı
   * projedeki dosyalara çözer. Yalnız çözülen yollar yanıtta yer alır.
   */
  resolve: (id: string, paths: string[]) =>
    api.post<Record<string, ResolvedAsset>>(`/api/files/${id}/resolve`, { paths }).then((r) => r.data),

  /** Eski biçimi modern biçime çevirir; sonuç YENİ bir dosyadır, orijinal korunur. */
  convert: (id: string) =>
    api.post<{ fileId: string; message: string }>(`/api/files/${id}/convert`)
      .then((r) => r.data),

  editorConfig: (id: string, mode: 'edit' | 'view') =>
    api.get<EditorConfigResponse>(`/api/files/${id}/editor-config`, { params: { mode } })
      .then((r) => r.data),

  tags: (id: string) => api.get<Tag[]>(`/api/files/${id}/tags`).then((r) => r.data),
  setTags: (id: string, tagIds: string[]) =>
    api.put<Tag[]>(`/api/files/${id}/tags`, { tagIds }).then((r) => r.data),
}

// --- paylaşım linkleri ------------------------------------------------
export const shareApi = {
  list: (targetType: ShareTargetType, targetId: string) =>
    api.get<ShareLink[]>('/api/share-links', { params: { targetType, targetId } })
      .then((r) => r.data),

  /** Yanıttaki `url` YALNIZCA burada dolu gelir; token bir daha gösterilemez. */
  create: (targetType: ShareTargetType, targetId: string, expiresInDays: number) =>
    api.post<ShareLink>('/api/share-links', { targetType, targetId, expiresInDays })
      .then((r) => r.data),

  revoke: (linkId: string) => api.delete(`/api/share-links/${linkId}`).then((r) => r.data),

  // --- genel erişim (oturum gerektirmez) ---
  view: (token: string) =>
    publicApi.get<SharedContent>(`/api/public/share/${token}`).then((r) => r.data),
  downloadUrl: (token: string) => `/api/public/share/${token}/download`,
  exportUrl: (token: string) => `/api/public/share/${token}/export`,
}

// --- klasörler --------------------------------------------------------
export const folderApi = {
  list: (projectId: string) =>
    api.get<Folder[]>(`/api/projects/${projectId}/folders`).then((r) => r.data),
  create: (projectId: string, name: string, parentId?: string | null) =>
    api.post<Folder>(`/api/projects/${projectId}/folders`, { name, parentId: parentId ?? null })
      .then((r) => r.data),
  /**
   * Klasör yüklemesi: göreli yolları ("A/B/C") parentId altında bulur, yoksa
   * oluşturur. Yanıt, gönderilen her yolu yaprak klasörün id'sine eşler.
   */
  ensurePaths: (projectId: string, paths: string[], parentId?: string | null) =>
    api.post<Record<string, string | null>>(`/api/projects/${projectId}/folders/paths`,
      { paths, parentId: parentId ?? null })
      .then((r) => r.data),
  update: (projectId: string, folderId: string, body: { name?: string; parentId?: string | null }) =>
    api.patch<Folder>(`/api/projects/${projectId}/folders/${folderId}`, body).then((r) => r.data),
  remove: (projectId: string, folderId: string) =>
    api.delete(`/api/projects/${projectId}/folders/${folderId}`).then((r) => r.data),
}

// --- etiketler --------------------------------------------------------
export const tagApi = {
  list: (projectId: string) =>
    api.get<Tag[]>(`/api/projects/${projectId}/tags`).then((r) => r.data),
  create: (projectId: string, name: string, color?: string) =>
    api.post<Tag>(`/api/projects/${projectId}/tags`, { name, color }).then((r) => r.data),
  remove: (projectId: string, tagId: string) =>
    api.delete(`/api/projects/${projectId}/tags/${tagId}`).then((r) => r.data),
}

// --- yorumlar ---------------------------------------------------------
export const commentApi = {
  list: (targetType: 'NOTE' | 'FILE', targetId: string) =>
    api.get<Comment[]>('/api/comments', { params: { targetType, targetId } }).then((r) => r.data),
  create: (targetType: 'NOTE' | 'FILE', targetId: string, body: string) =>
    api.post<Comment>('/api/comments', { targetType, targetId, body }).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/comments/${id}`).then((r) => r.data),
}

// --- proje kasası -----------------------------------------------------
export const vaultApi = {
  list: (projectId: string) =>
    api.get<VaultEntrySummary[]>(`/api/projects/${projectId}/vault`).then((r) => r.data),
  get: (projectId: string, entryId: string) =>
    api.get<VaultEntry>(`/api/projects/${projectId}/vault/${entryId}`).then((r) => r.data),
  create: (projectId: string, body: VaultEntryInput) =>
    api.post<VaultEntry>(`/api/projects/${projectId}/vault`, body).then((r) => r.data),
  update: (projectId: string, entryId: string, body: VaultEntryInput) =>
    api.put<VaultEntry>(`/api/projects/${projectId}/vault/${entryId}`, body).then((r) => r.data),
  remove: (projectId: string, entryId: string) =>
    api.delete(`/api/projects/${projectId}/vault/${entryId}`).then((r) => r.data),
}

// --- teklifler -------------------------------------------------------
const ofr = (projectId: string) => `/api/projects/${projectId}/offers`

export const offerApi = {
  /** Erişilen bütün projelerdeki teklifler (genel Teklifler sayfası). */
  all: () => api.get<OfferOverview>('/api/offers').then((r) => r.data),
  /** Seçili satırların durumu; yetkisi olmayan satır sunucuda atlanır. */
  bulkStatus: (ids: string[], status: OfferStatus) =>
    api.post<{ updated: number; skipped: number }>('/api/offers/bulk-status', { ids, status }).then((r) => r.data),

  list: (projectId: string) => api.get<OfferSummary[]>(ofr(projectId)).then((r) => r.data),
  get: (projectId: string, offerId: string) =>
    api.get<OfferDetail>(`${ofr(projectId)}/${offerId}`).then((r) => r.data),
  create: (projectId: string, body: OfferInput) =>
    api.post<OfferSummary>(ofr(projectId), body).then((r) => r.data),
  update: (projectId: string, offerId: string, body: OfferInput) =>
    api.put<OfferSummary>(`${ofr(projectId)}/${offerId}`, body).then((r) => r.data),
  remove: (projectId: string, offerId: string) =>
    api.delete(`${ofr(projectId)}/${offerId}`).then((r) => r.data),

  addNote: (projectId: string, offerId: string, body: string) =>
    api.post<OfferNote[]>(`${ofr(projectId)}/${offerId}/notes`, { body }).then((r) => r.data),
  updateNote: (projectId: string, offerId: string, noteId: string, body: string) =>
    api.put<OfferNote[]>(`${ofr(projectId)}/${offerId}/notes/${noteId}`, { body }).then((r) => r.data),
  removeNote: (projectId: string, offerId: string, noteId: string) =>
    api.delete<OfferNote[]>(`${ofr(projectId)}/${offerId}/notes/${noteId}`).then((r) => r.data),

  addDocuments: (projectId: string, offerId: string, files: File[]) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    return api
      .post<OfferDocument[]>(`${ofr(projectId)}/${offerId}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  removeDocument: (projectId: string, offerId: string, documentId: string) =>
    api.delete<OfferDocument[]>(`${ofr(projectId)}/${offerId}/documents/${documentId}`).then((r) => r.data),
  downloadDocument: async (projectId: string, offerId: string, documentId: string) => {
    const res = await api.get(`${ofr(projectId)}/${offerId}/documents/${documentId}/download`, { responseType: 'blob' })
    return { blob: res.data as Blob, filename: dosyaAdi(res.headers['content-disposition']) }
  },

  addReminder: (projectId: string, offerId: string, body: OfferReminderInput) =>
    api.post<OfferReminder[]>(`${ofr(projectId)}/${offerId}/reminders`, body).then((r) => r.data),
  updateReminder: (projectId: string, offerId: string, reminderId: string, body: OfferReminderInput) =>
    api.put<OfferReminder[]>(`${ofr(projectId)}/${offerId}/reminders/${reminderId}`, body).then((r) => r.data),
  removeReminder: (projectId: string, offerId: string, reminderId: string) =>
    api.delete<OfferReminder[]>(`${ofr(projectId)}/${offerId}/reminders/${reminderId}`).then((r) => r.data),
}

// --- uygulama içi bildirimler ------------------------------------------
export const notificationApi = {
  list: () => api.get<NotificationList>('/api/notifications').then((r) => r.data),
  markRead: (id: string) => api.post(`/api/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => api.post('/api/notifications/read-all').then((r) => r.data),
}

// --- deploy yardımcısı ------------------------------------------------
const dep = (projectId: string) => `/api/projects/${projectId}/deploy`

export const deployApi = {
  overview: (projectId: string) => api.get<DeployOverview>(dep(projectId)).then((r) => r.data),

  credentials: (projectId: string) =>
    api.get<DeployCredential[]>(`${dep(projectId)}/credentials`).then((r) => r.data),
  createCredential: (projectId: string, body: DeployCredentialInput) =>
    api.post<DeployCredential>(`${dep(projectId)}/credentials`, body).then((r) => r.data),
  updateCredential: (projectId: string, id: string, body: DeployCredentialInput) =>
    api.put<DeployCredential>(`${dep(projectId)}/credentials/${id}`, body).then((r) => r.data),
  removeCredential: (projectId: string, id: string) =>
    api.delete(`${dep(projectId)}/credentials/${id}`).then((r) => r.data),

  servers: (projectId: string) => api.get<DeployServer[]>(`${dep(projectId)}/servers`).then((r) => r.data),
  createServer: (projectId: string, body: DeployServerInput) =>
    api.post<DeployServer>(`${dep(projectId)}/servers`, body).then((r) => r.data),
  updateServer: (projectId: string, id: string, body: DeployServerInput) =>
    api.put<DeployServer>(`${dep(projectId)}/servers/${id}`, body).then((r) => r.data),
  removeServer: (projectId: string, id: string) =>
    api.delete(`${dep(projectId)}/servers/${id}`).then((r) => r.data),
  /** Bağlantı kurulamasa da 200 döner; sonuç `ok` alanındadır. */
  testServer: (projectId: string, id: string) =>
    api.post<DeployConnectionTest>(`${dep(projectId)}/servers/${id}/test`).then((r) => r.data),

  environments: (projectId: string) =>
    api.get<DeployEnvironment[]>(`${dep(projectId)}/environments`).then((r) => r.data),
  createEnvironment: (projectId: string, body: DeployEnvironmentInput) =>
    api.post<DeployEnvironment>(`${dep(projectId)}/environments`, body).then((r) => r.data),
  updateEnvironment: (projectId: string, id: string, body: DeployEnvironmentInput) =>
    api.put<DeployEnvironment>(`${dep(projectId)}/environments/${id}`, body).then((r) => r.data),
  removeEnvironment: (projectId: string, id: string) =>
    api.delete(`${dep(projectId)}/environments/${id}`).then((r) => r.data),

  job: (projectId: string, id: string) => api.get<DeployJob>(`${dep(projectId)}/jobs/${id}`).then((r) => r.data),
  createJob: (projectId: string, body: DeployJobInput) =>
    api.post<DeployJob>(`${dep(projectId)}/jobs`, body).then((r) => r.data),
  updateJob: (projectId: string, id: string, body: DeployJobInput) =>
    api.put<DeployJob>(`${dep(projectId)}/jobs/${id}`, body).then((r) => r.data),
  copyJob: (projectId: string, id: string, environmentId: string, name?: string) =>
    api.post<DeployJob>(`${dep(projectId)}/jobs/${id}/copy`, { environmentId, name }).then((r) => r.data),
  removeJob: (projectId: string, id: string) => api.delete(`${dep(projectId)}/jobs/${id}`).then((r) => r.data),

  start: (projectId: string, jobId: string) =>
    api.post<DeployRun>(`${dep(projectId)}/jobs/${jobId}/runs`).then((r) => r.data),
  runs: (projectId: string, params: { jobId?: string; page?: number; size?: number } = {}) =>
    api.get<Page<DeployRunSummary>>(`${dep(projectId)}/runs`, { params }).then((r) => r.data),
  run: (projectId: string, runId: string) =>
    api.get<DeployRun>(`${dep(projectId)}/runs/${runId}`).then((r) => r.data),
  logs: (projectId: string, runId: string, after: number) =>
    api.get<DeployLogPage>(`${dep(projectId)}/runs/${runId}/logs`, { params: { after, limit: 1000 } }).then((r) => r.data),
  answer: (projectId: string, runId: string, value: string) =>
    api.post(`${dep(projectId)}/runs/${runId}/answer`, { value }).then((r) => r.data),
  answerFile: (projectId: string, runId: string, file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData()
    form.append('file', file)
    return api.post(`${dep(projectId)}/runs/${runId}/answer-file`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
      },
    }).then((r) => r.data)
  },
  cancel: (projectId: string, runId: string) =>
    api.post(`${dep(projectId)}/runs/${runId}/cancel`).then((r) => r.data),
}

// --- görev panosu -----------------------------------------------------
export const taskApi = {
  /** Erişilen tüm projelerin görevleri; süzme istemcide. */
  all: () => api.get<TaskList>('/api/tasks').then((r) => r.data),
  board: (projectId: string) =>
    api.get<Board>(`/api/projects/${projectId}/board`).then((r) => r.data),
  /** Görev atanabilecek kişiler: doğrudan ya da ekip üzerinden erişen herkes. */
  assignees: (projectId: string) =>
    api.get<UserSummary[]>(`/api/projects/${projectId}/tasks/assignees`).then((r) => r.data),
  create: (projectId: string, body: TaskInput) =>
    api.post<Task>(`/api/projects/${projectId}/tasks`, body).then((r) => r.data),
  update: (projectId: string, taskId: string, body: TaskInput) =>
    api.put<Task>(`/api/projects/${projectId}/tasks/${taskId}`, body).then((r) => r.data),
  /** position boşsa görev sütunun sonuna eklenir. */
  move: (projectId: string, taskId: string, columnId: string, position?: number) =>
    api.patch<Task>(`/api/projects/${projectId}/tasks/${taskId}/move`, { columnId, position })
      .then((r) => r.data),
  remove: (projectId: string, taskId: string) =>
    api.delete(`/api/projects/${projectId}/tasks/${taskId}`).then((r) => r.data),
  teams: (projectId: string) =>
    api.get<AssignableTeam[]>(`/api/projects/${projectId}/tasks/assignable-teams`).then((r) => r.data),

  // --- görev ayrıntısı ---
  detail: (projectId: string, taskId: string) =>
    api.get<TaskDetail>(`/api/projects/${projectId}/tasks/${taskId}`).then((r) => r.data),
  history: (projectId: string, taskId: string) =>
    api.get<TaskHistoryEntry[]>(`/api/projects/${projectId}/tasks/${taskId}/history`).then((r) => r.data),

  addChecklistItem: (projectId: string, taskId: string, text: string) =>
    api.post<ChecklistItem[]>(`/api/projects/${projectId}/tasks/${taskId}/checklist`, { text })
      .then((r) => r.data),
  updateChecklistItem: (projectId: string, taskId: string, itemId: string,
                        body: { text?: string; done?: boolean }) =>
    api.patch<ChecklistItem[]>(`/api/projects/${projectId}/tasks/${taskId}/checklist/${itemId}`, body)
      .then((r) => r.data),
  removeChecklistItem: (projectId: string, taskId: string, itemId: string) =>
    api.delete<ChecklistItem[]>(`/api/projects/${projectId}/tasks/${taskId}/checklist/${itemId}`)
      .then((r) => r.data),

  addAttachments: (projectId: string, taskId: string, files: File[],
                   onProgress?: (pct: number) => void) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    return api
      .post<TaskAttachment[]>(`/api/projects/${projectId}/tasks/${taskId}/attachments`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
        },
      })
      .then((r) => r.data)
  },
  removeAttachment: (projectId: string, taskId: string, attachmentId: string) =>
    api.delete(`/api/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}`).then((r) => r.data),

  comments: (projectId: string, taskId: string) =>
    api.get<TaskComment[]>(`/api/projects/${projectId}/tasks/${taskId}/comments`).then((r) => r.data),
  addComment: (projectId: string, taskId: string, body: string, files: File[]) => {
    const form = new FormData()
    form.append('body', body)
    files.forEach((f) => form.append('files', f))
    return api
      .post<TaskComment>(`/api/projects/${projectId}/tasks/${taskId}/comments`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  editComment: (projectId: string, taskId: string, commentId: string, body: string) =>
    api.put<TaskComment>(`/api/projects/${projectId}/tasks/${taskId}/comments/${commentId}`, { body })
      .then((r) => r.data),
  removeComment: (projectId: string, taskId: string, commentId: string) =>
    api.delete(`/api/projects/${projectId}/tasks/${taskId}/comments/${commentId}`).then((r) => r.data),

  createColumn: (projectId: string, body: ColumnInput) =>
    api.post<TaskColumn>(`/api/projects/${projectId}/task-columns`, body).then((r) => r.data),
  updateColumn: (projectId: string, columnId: string, body: ColumnInput) =>
    api.put<TaskColumn>(`/api/projects/${projectId}/task-columns/${columnId}`, body).then((r) => r.data),
  reorderColumns: (projectId: string, ids: string[]) =>
    api.put<TaskColumn[]>(`/api/projects/${projectId}/task-columns/order`, { ids }).then((r) => r.data),
  removeColumn: (projectId: string, columnId: string) =>
    api.delete(`/api/projects/${projectId}/task-columns/${columnId}`).then((r) => r.data),
}

// --- proje durumları --------------------------------------------------
export const stageApi = {
  overview: () => api.get<Overview>('/api/project-overview').then((r) => r.data),
  list: () => api.get<ProjectStage[]>('/api/project-stages').then((r) => r.data),
  create: (body: { name: string; color: string }) =>
    api.post<ProjectStage>('/api/project-stages', body).then((r) => r.data),
  update: (id: string, body: { name: string; color: string }) =>
    api.put<ProjectStage>(`/api/project-stages/${id}`, body).then((r) => r.data),
  reorder: (ids: string[]) =>
    api.put<ProjectStage[]>('/api/project-stages/order', { ids }).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/project-stages/${id}`).then((r) => r.data),

  progress: (projectId: string) =>
    api.get<ProjectProgress>(`/api/projects/${projectId}/progress`).then((r) => r.data),
  setProgress: (projectId: string, stageId: string | null, note: string | null) =>
    api.put<ProjectProgress>(`/api/projects/${projectId}/progress`, { stageId, note })
      .then((r) => r.data),
}

// --- arama ------------------------------------------------------------
export const searchApi = {
  search: (q: string, params: { projectId?: string; type?: string; limit?: number } = {}) =>
    api.get<SearchHit[]>('/api/search', { params: { q, ...params } }).then((r) => r.data),
}

// --- kullanıcı dizini -------------------------------------------------
export const userApi = {
  search: (q: string) =>
    api.get<UserSummary[]>('/api/users/search', { params: { q, limit: 20 } }).then((r) => r.data),
}

// --- yönetim ----------------------------------------------------------
// --- ekipler ----------------------------------------------------------
/** Ekip seçicisi — her kullanıcıya açık, yalnızca ad ve kişi sayısı döner. */
export const teamApi = {
  search: (q: string) =>
    api.get<TeamSummary[]>('/api/teams/search', { params: { q, limit: 20 } }).then((r) => r.data),
}

/** Ekip yönetimi — yalnızca yöneticiler. */
export const adminTeamApi = {
  list: (params: { q?: string; page?: number; size?: number }) =>
    api.get<Page<Team>>('/api/admin/teams', { params }).then((r) => r.data),
  get: (id: string) => api.get<TeamDetail>(`/api/admin/teams/${id}`).then((r) => r.data),
  create: (body: { name: string; description?: string }) =>
    api.post<Team>('/api/admin/teams', body).then((r) => r.data),
  update: (id: string, body: { name?: string; description?: string; version?: number }) =>
    api.patch<Team>(`/api/admin/teams/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/admin/teams/${id}`).then((r) => r.data),
  addMember: (id: string, userId: string) =>
    api.post(`/api/admin/teams/${id}/members`, { userId }).then((r) => r.data),
  removeMember: (id: string, userId: string) =>
    api.delete(`/api/admin/teams/${id}/members/${userId}`).then((r) => r.data),
}

// --- roller ve izinler -------------------------------------------------
export const roleApi = {
  /** İzin kataloğu: ad, açıklama, grup, bağımlılık. Her oturum açmış kullanıcıya açık. */
  permissions: () => api.get<PermissionDef[]>('/api/permissions').then((r) => r.data),
  /** Rol seçicileri için (proje rolleri üye ekleyene de gerekir). */
  list: (scope?: RoleScope) => api.get<Role[]>('/api/roles', { params: { scope } }).then((r) => r.data),
  create: (body: RoleInput & { scope: RoleScope }) => api.post<Role>('/api/admin/roles', body).then((r) => r.data),
  update: (id: string, body: RoleInput) => api.put<Role>(`/api/admin/roles/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/admin/roles/${id}`).then((r) => r.data),
}

/** Yönetim › Tüm projeler (`admin.projects.members`): içerik değil, üye yönetimi. */
export const adminProjectApi = {
  list: (params: { q?: string; page?: number; size?: number }) =>
    api.get<Page<AdminProject>>('/api/admin/projects', { params }).then((r) => r.data),
  get: (id: string) => api.get<AdminProject>(`/api/admin/projects/${id}`).then((r) => r.data),
}

export const adminApi = {
  users: (params: { q?: string; status?: string; role?: string; page?: number; size?: number }) =>
    api.get<Page<AdminUser>>('/api/admin/users', { params }).then((r) => r.data),
  /** Sistem rolleri {@code roleIds} ile; "Kullanıcı" rolünü sunucu her zaman ekler. */
  /** {@code sendInvite}: parola verilmez; kullanıcı parolasını e-postadaki bağlantıyla belirler. */
  createUser: (body: {
    username: string; displayName: string; email?: string; password?: string; sendInvite?: boolean
    roles?: string[]; roleIds?: string[]
  }) => api.post<AdminUser>('/api/admin/users', body).then((r) => r.data),
  updateUser: (id: string, body: { displayName?: string; email?: string; roles?: string[]; roleIds?: string[] }) =>
    api.patch<AdminUser>(`/api/admin/users/${id}`, body).then((r) => r.data),
  enable: (id: string) => api.post(`/api/admin/users/${id}/enable`).then((r) => r.data),
  disable: (id: string) => api.post(`/api/admin/users/${id}/disable`).then((r) => r.data),
  resetPassword: (id: string, newPassword: string) =>
    api.post(`/api/admin/users/${id}/reset-password`, { newPassword }).then((r) => r.data),
  forceLogout: (id: string) => api.post(`/api/admin/users/${id}/force-logout`).then((r) => r.data),
  /** Parola belirleme bağlantısını kullanıcının e-postasına (yeniden) gönderir. */
  invite: (id: string) => api.post(`/api/admin/users/${id}/invite`).then((r) => r.data),
  /** Kapatmak yalnız mobil oturumları sonlandırır; web oturumları sürer. */
  setMobileAccess: (id: string, enabled: boolean) =>
    api.put<AdminUser>(`/api/admin/users/${id}/mobile-access`, { enabled }).then((r) => r.data),
  sessions: (id: string) =>
    api.get<Session[]>(`/api/admin/users/${id}/sessions`).then((r) => r.data),

  /** {@code actions}/{@code notActions} virgülle ayrılır (sunucu listeye çevirir). */
  audit: (params: { action?: string; actions?: string; notActions?: string; page?: number; size?: number }) =>
    api.get<Page<AuditEntry>>('/api/admin/audit', { params }).then((r) => r.data),
  stats: () => api.get<Record<string, number>>('/api/admin/stats').then((r) => r.data),
  storageUsage: () => api.get<Record<string, number>>('/api/admin/storage/usage').then((r) => r.data),
}

// --- e-posta ayarları (Yönetim › E-posta) --------------------------------
export const mailApi = {
  get: () => api.get<MailSettings>('/api/admin/mail').then((r) => r.data),
  update: (body: MailSettingsInput) => api.put<MailSettings>('/api/admin/mail', body).then((r) => r.data),
  /** Yalnız değişen türler gönderilir: {@code { TASK_ASSIGNED: false }}. */
  updateEvents: (events: Record<string, boolean>) =>
    api.put<MailEventSetting[]>('/api/admin/mail/events', { events }).then((r) => r.data),
  /** Kayıtlı ayarla sınama postası; başarısızsa sunucunun verdiği neden hata metninde döner. */
  test: (to: string) => api.post('/api/admin/mail/test', { to }).then((r) => r.data),
  /** Günlük iş özetini şimdi gönderir (normalde her sabah 07:00); posta kuyruğa alınan kişi sayısı döner. */
  sendDigest: () => api.post<{ recipients: number }>('/api/admin/mail/digest').then((r) => r.data),
  log: () => api.get<MailLogEntry[]>('/api/admin/mail/log').then((r) => r.data),
}

// --- test takibi (UAT) -------------------------------------------------

/** Tarayıcının saat dilimi: "bugün" ve günlük rapor kullanıcının gününe göre bölünür. */
const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone

export interface RunInput {
  name: string
  appVersion?: string | null
  environment?: string | null
  startsOn?: string | null
  endsOn?: string | null
}

export const testApi = {
  suites: (projectId: string) =>
    api.get<TestSuite[]>(`/api/projects/${projectId}/tests/suites`).then((r) => r.data),
  /** suiteId verilirse o set güncellenir (girilmiş sonuçlar korunur). */
  /** Senaryo Excel'i (.xlsx). */
  importXlsx: (projectId: string, file: File, opts: { suiteId?: string; name?: string } = {}) => {
    const form = new FormData()
    form.append('file', file)
    if (opts.suiteId) form.append('suiteId', opts.suiteId)
    if (opts.name) form.append('name', opts.name)
    return api
      .post<TestImportResult>(`/api/projects/${projectId}/tests/suites/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  /** Formatı gösteren, doldurulmuş örnek senaryo Excel'i (.xlsx). */
  template: async (projectId: string) => {
    const res = await api.get(`/api/projects/${projectId}/tests/suites/template`, { responseType: 'blob' })
    return { blob: res.data as Blob, filename: dosyaAdi(res.headers['content-disposition']) }
  },
  deleteSuite: (projectId: string, suiteId: string) =>
    api.delete(`/api/projects/${projectId}/tests/suites/${suiteId}`).then((r) => r.data),

  runs: (projectId: string) =>
    api.get<TestRun[]>(`/api/projects/${projectId}/tests/runs`).then((r) => r.data),
  createRun: (projectId: string, body: RunInput & { suiteId: string }) =>
    api.post<TestRun>(`/api/projects/${projectId}/tests/runs`, body).then((r) => r.data),
  updateRun: (projectId: string, runId: string,
              body: RunInput & { status: RunStatus; version: number }) =>
    api.patch<TestRun>(`/api/projects/${projectId}/tests/runs/${runId}`, body).then((r) => r.data),
  deleteRun: (projectId: string, runId: string) =>
    api.delete(`/api/projects/${projectId}/tests/runs/${runId}`).then((r) => r.data),

  modules: (projectId: string, runId: string) =>
    api.get<TestModuleProgress[]>(`/api/projects/${projectId}/tests/runs/${runId}/modules`)
      .then((r) => r.data),
  results: (projectId: string, runId: string, f: TestCaseFilter) =>
    api.get<Page<TestCaseResult>>(`/api/projects/${projectId}/tests/runs/${runId}/results`, {
      params: { ...f, status: f.status?.length ? f.status.join(',') : undefined },
    }).then((r) => r.data),
  history: (projectId: string, runId: string, resultId: string) =>
    api.get<TestResultEvent[]>(
      `/api/projects/${projectId}/tests/runs/${runId}/results/${resultId}/history`,
    ).then((r) => r.data),
  claim: (projectId: string, runId: string, resultId: string) =>
    api.post<TestCaseResult>(
      `/api/projects/${projectId}/tests/runs/${runId}/results/${resultId}/claim`,
    ).then((r) => r.data),
  record: (projectId: string, runId: string, resultId: string,
           body: { status: TestStatus; note?: string | null; bugRef?: string | null; version: number }) =>
    api.put<TestCaseResult>(
      `/api/projects/${projectId}/tests/runs/${runId}/results/${resultId}`, body,
    ).then((r) => r.data),
  /** Test edenin sırası: sıradaki senaryo, atladıkları, kalan/tamamlanan sayıları. */
  queue: (projectId: string, runId: string) =>
    api.get<TestQueue>(`/api/projects/${projectId}/tests/runs/${runId}/queue`).then((r) => r.data),
  skipCase: (projectId: string, runId: string, resultId: string) =>
    api.post<TestQueue>(`/api/projects/${projectId}/tests/runs/${runId}/queue/${resultId}/skip`)
      .then((r) => r.data),
  resumeCase: (projectId: string, runId: string, resultId: string) =>
    api.post<TestQueue>(`/api/projects/${projectId}/tests/runs/${runId}/queue/${resultId}/resume`)
      .then((r) => r.data),
  assignModule: (projectId: string, runId: string, moduleId: string, userId: string | null) =>
    api.put<TestModuleProgress>(
      `/api/projects/${projectId}/tests/runs/${runId}/modules/${moduleId}/assignee`, { userId },
    ).then((r) => r.data),
  assignResult: (projectId: string, runId: string, resultId: string, userId: string | null) =>
    api.put<TestCaseResult>(
      `/api/projects/${projectId}/tests/runs/${runId}/results/${resultId}/assignee`, { userId },
    ).then((r) => r.data),

  testers: (projectId: string, runId: string) =>
    api.get<TesterActivity[]>(`/api/projects/${projectId}/tests/runs/${runId}/testers`,
      { params: { tz: tz() } }).then((r) => r.data),
  daily: (projectId: string, runId: string, date?: string) =>
    api.get<DailyReport>(`/api/projects/${projectId}/tests/runs/${runId}/reports/daily`,
      { params: { date, tz: tz() } }).then((r) => r.data),

  /** Koşumun tüm senaryoları + sonuçları. Bkz. noteApi.download: token bellekte, düz link olmaz. */
  exportRun: async (projectId: string, runId: string, format: 'xlsx' | 'csv') => {
    const res = await api.get(`/api/projects/${projectId}/tests/runs/${runId}/export`, {
      params: { format, tz: tz() }, responseType: 'blob',
    })
    return { blob: res.data as Blob, filename: dosyaAdi(res.headers['content-disposition']) }
  },
  exportDaily: async (projectId: string, runId: string, date: string, format: 'xlsx' | 'csv') => {
    const res = await api.get(`/api/projects/${projectId}/tests/runs/${runId}/reports/daily/export`, {
      params: { date, format, tz: tz() }, responseType: 'blob',
    })
    return { blob: res.data as Blob, filename: dosyaAdi(res.headers['content-disposition']) }
  },
}
